import { useRef } from 'react';
import type { BrandWizardStep } from '@mg/shared';
import { Button } from '../../components/ui/Button';
import { STICKY_FOOTER_HEIGHT_VAR } from '../../components/ui/toast';
import { TOUCH_TARGET } from '../../components/ui/touchTarget';
import { useIsoLayoutEffect } from '../../lib/useIsoLayoutEffect';
import { isLastStep } from './steps';
import { BAR_COPY } from './wizardCopy';

/** Which bar action is running (its button shows the spinner; every other one waits). */
export type BarAction = 'back' | 'skip' | 'next' | 'finish';

export interface StepErrorState {
  message: string;
  /** A version conflict: the bar offers Reload. */
  conflict: boolean;
}

/**
 * The wizard's bar (spec:97-99; web.md §4.1): one row always in view under the step, Back (not on step 1)
 * and Exit onboarding on the left, Skip for now and Next on the right, Finish setup in their place on
 * step 8. Only Next and Finish setup are filled. Every button waits while a save or an upload runs, and
 * a step save that failed says why above the row (with Reload after a version conflict).
 *
 * It keeps its height in STICKY_FOOTER_HEIGHT_VAR on <html> while it is shown, exactly as the settings
 * pages' StickySaveFooter does, so the app's toast sits above it.
 */
export function WizardBar({
  step,
  busy,
  locked,
  error,
  onBack,
  onExit,
  onSkip,
  onNext,
  onReload,
}: {
  step: BrandWizardStep;
  /** The bar action running now, if any. */
  busy: BarAction | null;
  /** Something else is running (an upload, an exit, a reload): every button waits. */
  locked: boolean;
  error: StepErrorState | null;
  onBack: () => void;
  onExit: () => void;
  onSkip: () => void;
  onNext: () => void;
  onReload: () => void;
}) {
  const bar = useRef<HTMLDivElement>(null);

  useIsoLayoutEffect(() => {
    const el = bar.current;
    if (!el) return;
    const root = document.documentElement;
    const write = () => root.style.setProperty(STICKY_FOOTER_HEIGHT_VAR, `${el.getBoundingClientRect().height}px`);
    write();
    // Written in the next frame, never inside the observer's callback, as StickySaveFooter does.
    let frame: number | null = null;
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (frame !== null) return;
            frame = requestAnimationFrame(() => {
              frame = null;
              write();
            });
          });
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      root.style.removeProperty(STICKY_FOOTER_HEIGHT_VAR);
    };
  }, []);

  const waiting = busy !== null || locked;
  const last = isLastStep(step);

  return (
    <div
      ref={bar}
      data-wizard-bar=""
      // `scrollbar-gutter: stable` keeps the bar's column in line with the scroll region's above it,
      // which always draws its track.
      className="shrink-0 overflow-y-auto border-t border-line bg-card [scrollbar-gutter:stable]"
    >
      <div className="mx-auto w-full max-w-[1440px] px-6">
        <div className="mx-auto w-full max-w-[60rem] py-3">
          {error && (
            <div role="alert" className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-red-600 dark:text-error-dark">
              <p className="min-w-0">{error.message}</p>
              {error.conflict && (
                <Button type="button" variant="secondary" size="sm" disabled={waiting} onClick={onReload}>
                  {BAR_COPY.reload}
                </Button>
              )}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              {step > 1 && (
                <Button
                  type="button"
                  variant="plain"
                  className={TOUCH_TARGET}
                  loading={busy === 'back'}
                  disabled={waiting}
                  onClick={onBack}
                >
                  {BAR_COPY.back}
                </Button>
              )}
              <Button
                type="button"
                variant="plain"
                className={TOUCH_TARGET}
                // The visible label is "Exit" on a phone; the name keeps all of it (WCAG 2.5.3).
                aria-label={BAR_COPY.exit}
                disabled={waiting}
                onClick={onExit}
              >
                <span className="sm:hidden">{BAR_COPY.exitShort}</span>
                <span className="hidden sm:inline">{BAR_COPY.exit}</span>
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {last ? (
                <Button type="button" className={TOUCH_TARGET} loading={busy === 'finish'} disabled={waiting} onClick={onNext}>
                  {BAR_COPY.finish}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="plain"
                    className={TOUCH_TARGET}
                    loading={busy === 'skip'}
                    disabled={waiting}
                    onClick={onSkip}
                  >
                    {BAR_COPY.skip}
                  </Button>
                  <Button type="button" className={TOUCH_TARGET} loading={busy === 'next'} disabled={waiting} onClick={onNext}>
                    {BAR_COPY.next}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
